// Hook para manejar el estado de la auditoría con soporte offline
// Sprint 3 - AuditExecutor

import { useState, useEffect, useCallback, useRef } from 'react';
import { Audit as Auditoria, QuestionResponse } from '../types/audit.types';
import auditService from '../services/auditService';
import { generateUUID } from '../utils/uuid';

const AUTOSAVE_DELAY = 3000; // 3 segundos
const STORAGE_KEY_PREFIX = 'audit_';

interface UseAuditStateOptions {
  auditoriaId: number;
  onSaveSuccess?: () => void;
  onSaveError?: (error: Error) => void;
}

interface UseAuditStateReturn {
  auditoria: Auditoria | null;
  formulario: any | null;
  seccionActual: number;
  respuestas: Map<number, QuestionResponse>;
  loading: boolean;
  saving: boolean;
  error: string | null;
  isOnline: boolean;
  lastSaved: Date | null;
  
  // Actions
  setRespuesta: (preguntaId: number, categoriaId: number, valor: any, comentario?: string) => void;
  setSeccionActual: (seccionId: number) => void;
  guardarManual: () => Promise<void>;
  completarAuditoria: (firmaUrl: string) => Promise<void>;
  getProgreso: () => { total: number; respondidas: number; porcentaje: number };
}

export const useAuditState = ({
  auditoriaId,
  onSaveSuccess,
  onSaveError,
}: UseAuditStateOptions): UseAuditStateReturn => {
  const [auditoria, setAuditoria] = useState<Auditoria | null>(null);
  const [formulario, setFormulario] = useState<any | null>(null);
  const [seccionActual, setSeccionActual] = useState<number>(0);
  const [respuestas, setRespuestas] = useState<Map<number, QuestionResponse>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const autoSaveTimeoutRef = useRef<number | null>(null);
  const hasUnsavedChangesRef = useRef(false);

  // Storage keys
  const storageKey = `${STORAGE_KEY_PREFIX}${auditoriaId}`;
  const responsesKey = `${storageKey}_responses`;

  // =======================
  // Online/Offline Detection
  // =======================
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // =======================
  // Load Initial Data
  // =======================
  useEffect(() => {
    const loadAuditoria = async () => {
      try {
        setLoading(true);
        setError(null);

        // Intentar cargar desde el servidor
        if (isOnline) {
          const data = await auditService.getById(auditoriaId);
          setAuditoria(data.audit);
          setFormulario(data.form);

          // Cargar respuestas existentes
          if (data.responses && data.responses.length > 0) {
            const responsesMap = new Map<number, QuestionResponse>();
            data.responses.forEach((resp: any) => {
              responsesMap.set(resp.question_id, {
                question_id: resp.question_id,
                category_id: resp.category_id,
                response_value: resp.response_value,
                comment: resp.comment || undefined,
              });
            });
            setRespuestas(responsesMap);
            // Guardar en localStorage
            localStorage.setItem(responsesKey, JSON.stringify(Array.from(responsesMap.entries())));
          }

          // Guardar en localStorage para offline
          localStorage.setItem(storageKey, JSON.stringify(data));
          
          // Establecer primera sección como actual
          if (data.form?.sections?.length > 0) {
            setSeccionActual(data.form.sections[0].id);
          }
        } else {
          // Modo offline: cargar desde localStorage
          const cachedData = localStorage.getItem(storageKey);
          if (cachedData) {
            const data = JSON.parse(cachedData);
            setAuditoria(data.audit);
            setFormulario(data.form);
            
            if (data.form?.sections?.length > 0) {
              setSeccionActual(data.form.sections[0].id);
            }
          } else {
            throw new Error('No hay datos offline disponibles para esta auditoría');
          }

          // Cargar respuestas desde localStorage
          const cachedResponses = localStorage.getItem(responsesKey);
          if (cachedResponses) {
            const responsesArray = JSON.parse(cachedResponses);
            setRespuestas(new Map(responsesArray));
          }
        }
      } catch (err: any) {
        console.error('Error cargando auditoría:', err);
        setError(err.message || 'Error al cargar la auditoría');
      } finally {
        setLoading(false);
      }
    };

    loadAuditoria();
  }, [auditoriaId, isOnline]);

  // =======================
  // Auto-Save Logic
  // =======================
  const guardarRespuestas = useCallback(async () => {
    if (!auditoria || respuestas.size === 0) return;

    try {
      setSaving(true);

      // Convertir Map a array de respuestas
      const respuestasArray = Array.from(respuestas.values()).map(resp => ({
        question_id: resp.question_id,
        category_id: resp.category_id,
        response_value: resp.response_value,
        comment: resp.comment,
      }));

      if (isOnline) {
        // Guardar en el servidor
        await auditService.update(auditoriaId, {
          responses: respuestasArray,
        });
        setLastSaved(new Date());
        onSaveSuccess?.();
      }

      // Siempre guardar en localStorage (offline backup)
      localStorage.setItem(responsesKey, JSON.stringify(Array.from(respuestas.entries())));
      hasUnsavedChangesRef.current = false;
    } catch (err: any) {
      console.error('Error guardando respuestas:', err);
      onSaveError?.(err);
      
      // En caso de error online, guardar en localStorage de todas formas
      localStorage.setItem(responsesKey, JSON.stringify(Array.from(respuestas.entries())));
    } finally {
      setSaving(false);
    }
  }, [auditoria, auditoriaId, respuestas, isOnline, onSaveSuccess, onSaveError]);

  // =======================
  // Set Respuesta (con auto-save)
  // =======================
  const setRespuesta = useCallback((
    preguntaId: number,
    categoriaId: number,
    valor: any,
    comentario?: string
  ) => {
    setRespuestas(prev => {
      const newMap = new Map(prev);
      newMap.set(preguntaId, {
        question_id: preguntaId,
        category_id: categoriaId,
        response_value: valor,
        comment: comentario,
      });
      return newMap;
    });

    // Marcar como cambios sin guardar
    hasUnsavedChangesRef.current = true;

    // Cancelar timeout anterior y crear uno nuevo
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      guardarRespuestas();
    }, AUTOSAVE_DELAY);
  }, [guardarRespuestas]);

  // =======================
  // Guardar Manual
  // =======================
  const guardarManual = useCallback(async () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    await guardarRespuestas();
  }, [guardarRespuestas]);

  // =======================
  // Completar Auditoría
  // =======================
  const completarAuditoria = useCallback(async (firmaUrl: string) => {
    if (!auditoria) return;

    try {
      setSaving(true);

      // Primero guardar respuestas pendientes
      if (hasUnsavedChangesRef.current) {
        await guardarManual();
      }

      // Luego completar la auditoría
      if (isOnline) {
        await auditService.complete(auditoriaId, { signature_selfie_url: firmaUrl });
      } else {
        // En modo offline, marcar para sincronizar después
        const syncQueue = JSON.parse(localStorage.getItem('sync_queue') || '[]');
        syncQueue.push({
          id: generateUUID(),
          tipo: 'auditoria',
          accion: 'complete',
          data: { auditoriaId, firmaUrl },
          intentos: 0,
        });
        localStorage.setItem('sync_queue', JSON.stringify(syncQueue));
      }

      // Limpiar localStorage de esta auditoría
      localStorage.removeItem(storageKey);
      localStorage.removeItem(responsesKey);
    } catch (err: any) {
      console.error('Error completando auditoría:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [auditoria, auditoriaId, isOnline, guardarManual]);

  // =======================
  // Calcular Progreso
  // =======================
  const getProgreso = useCallback(() => {
    if (!formulario) {
      return { total: 0, respondidas: 0, porcentaje: 0 };
    }

    const totalPreguntas = formulario.sections?.reduce(
      (sum: number, section: any) => sum + (section.questions?.length || 0),
      0
    ) || 0;

    const preguntasRespondidas = respuestas.size;
    const porcentaje = totalPreguntas > 0 ? (preguntasRespondidas / totalPreguntas) * 100 : 0;

    return {
      total: totalPreguntas,
      respondidas: preguntasRespondidas,
      porcentaje: Math.round(porcentaje),
    };
  }, [formulario, respuestas]);

  // =======================
  // Cleanup on unmount
  // =======================
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      
      // Guardar cambios pendientes antes de desmontar
      if (hasUnsavedChangesRef.current && respuestas.size > 0) {
        localStorage.setItem(responsesKey, JSON.stringify(Array.from(respuestas.entries())));
      }
    };
  }, [respuestas, responsesKey]);

  return {
    auditoria,
    formulario,
    seccionActual,
    respuestas,
    loading,
    saving,
    error,
    isOnline,
    lastSaved,
    setRespuesta,
    setSeccionActual,
    guardarManual,
    completarAuditoria,
    getProgreso,
  };
};
